import axios from 'axios';
import { infoLog } from '../utils/logger';

const resolveDefaultBaseUrl = () => {
  if (typeof window === 'undefined') {
    return 'http://localhost:8000/api/v1';
  }

  // Default to same-origin proxying in production (e.g. nginx serves `/api/v1`).
  // In local dev, prefer the backend port unless explicitly overridden via env.
  const { hostname, protocol } = window.location;
  const fallbackPort = process.env.REACT_APP_API_PORT || '8000';

  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
  if (isLocalhost) {
    return `${protocol}//${hostname}:${fallbackPort}/api/v1`;
  }

  return '/api/v1';
};

const normalizeApiBaseUrl = (value) => {
  if (!value) {
    return null;
  }

  const trimmed = value.replace(/\/+$/, '');
  if (trimmed.endsWith('/api/v1')) {
    return trimmed;
  }

  if (trimmed.endsWith('/api')) {
    return `${trimmed}/v1`;
  }

  return `${trimmed}/api/v1`;
};

const resolvedEnvBaseUrl = normalizeApiBaseUrl(process.env.REACT_APP_API_BASE_URL);
const API_BASE_URL = resolvedEnvBaseUrl || resolveDefaultBaseUrl();

if (typeof window !== 'undefined') {
  infoLog('[AuthService] Using API base URL:', API_BASE_URL);
}

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {},
});

const SESSION_KEYS = [
  'accessToken',
  'refreshToken',
  'user',
  'sessionPersistence',
  'sessionMetadata',
];

const hasWindow = () => typeof window !== 'undefined';

const storageHasKey = (storage, key) => storage.getItem(key) !== null;

const storageHasSessionData = (storage) =>
  SESSION_KEYS.some((key) => storageHasKey(storage, key));

const getActiveSessionStorage = () => {
  if (!hasWindow()) {
    return null;
  }

  const localPersistence = window.localStorage.getItem('sessionPersistence');
  const sessionPersistence = window.sessionStorage.getItem('sessionPersistence');

  // A remembered login should take precedence across tabs even if a stale
  // sessionStorage token still exists in the current tab.
  if (localPersistence === 'remember') {
    return window.localStorage;
  }

  if (sessionPersistence === 'session') {
    return window.sessionStorage;
  }

  const localHasSession = storageHasSessionData(window.localStorage);
  const sessionHasSession = storageHasSessionData(window.sessionStorage);

  if (localHasSession && !sessionHasSession) {
    return window.localStorage;
  }

  if (sessionHasSession && !localHasSession) {
    return window.sessionStorage;
  }

  if (localHasSession && sessionHasSession) {
    return window.localStorage;
  }

  return null;
};

const getStoredItem = (key) => {
  const storage = getActiveSessionStorage();
  return storage ? storage.getItem(key) : null;
};

const clearStorageKeys = (storage) => {
  if (!storage) {
    return;
  }
  SESSION_KEYS.forEach((key) => storage.removeItem(key));
};

const getPersistenceStorage = (rememberMe) => {
  if (!hasWindow()) {
    return null;
  }
  return rememberMe ? window.localStorage : window.sessionStorage;
};

const persistSession = ({
  accessToken,
  refreshToken,
  user,
  rememberMe,
  sessionMetadata,
}) => {
  if (!hasWindow()) {
    return;
  }

  const storage = getPersistenceStorage(rememberMe);
  clearStorageKeys(window.localStorage);
  clearStorageKeys(window.sessionStorage);

  if (accessToken) {
    storage.setItem('accessToken', accessToken);
  }
  if (refreshToken) {
    storage.setItem('refreshToken', refreshToken);
  }
  if (user) {
    storage.setItem('user', JSON.stringify(user));
  }
  storage.setItem('sessionPersistence', rememberMe ? 'remember' : 'session');
  if (sessionMetadata) {
    storage.setItem('sessionMetadata', JSON.stringify(sessionMetadata));
  }
};

const updateSessionTokens = ({
  accessToken,
  refreshToken,
  sessionMetadata,
}) => {
  if (!hasWindow()) {
    return;
  }

  const storage = getActiveSessionStorage() || window.localStorage;

  if (accessToken) {
    storage.setItem('accessToken', accessToken);
  }
  if (refreshToken) {
    storage.setItem('refreshToken', refreshToken);
  } else {
    storage.removeItem('refreshToken');
  }
  if (sessionMetadata) {
    storage.setItem('sessionMetadata', JSON.stringify(sessionMetadata));
  }
};

export const getAccessToken = () => getStoredItem('accessToken');

// Add a request interceptor to include the auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      if (config.headers && typeof config.headers.set === 'function') {
        config.headers.set('Authorization', `Bearer ${token}`);
      } else {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Let the browser set the correct multipart boundary for FormData requests.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      if (config.headers && typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
      }
      if (config.headers) {
        delete config.headers['Content-Type'];
        delete config.headers['content-type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const clearSession = () => {
  if (!hasWindow()) {
    return;
  }
  clearStorageKeys(window.localStorage);
  clearStorageKeys(window.sessionStorage);
};

const notifyLogout = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('auth:logout'));
  }
};

let refreshInFlight = null;

// Add a response interceptor to handle token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const requestUrl = typeof originalRequest?.url === 'string' ? originalRequest.url : '';
    const isRefreshRequest = requestUrl.includes('/auth/refresh');
    const isAuthRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');
    
    // If error is 401 and we haven't tried to refresh token yet
    if (error.response?.status === 401 && !originalRequest?._retry && !isRefreshRequest && !isAuthRequest) {
      originalRequest._retry = true;
      
      try {
        if (!refreshInFlight) {
          const refreshToken = getStoredItem('refreshToken');
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          refreshInFlight = apiClient
            .post('/auth/refresh', { refresh_token: refreshToken })
            .then((response) => {
              const { access_token, refresh_token } = response.data;
              updateSessionTokens({
                accessToken: access_token,
                refreshToken: refresh_token || null,
                sessionMetadata: {
                  session_persistence: response.data.session_persistence,
                  access_expires_at: response.data.access_expires_at,
                  refresh_expires_at: response.data.refresh_expires_at,
                  session_expires_at: response.data.session_expires_at,
                },
              });
              return access_token;
            })
            .finally(() => {
              refreshInFlight = null;
            });
        }

        const accessToken = await refreshInFlight;

        // Retry the original request
        if (originalRequest.headers && typeof originalRequest.headers.set === 'function') {
          originalRequest.headers.set('Authorization', `Bearer ${accessToken}`);
        } else {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }
        return apiClient(originalRequest);
      } catch (refreshError) {
        clearSession();
        notifyLogout();
      }
    }

    if (error.response?.status === 401 && isRefreshRequest) {
      clearSession();
      notifyLogout();
    }
    
    return Promise.reject(error);
  }
);

const authService = {
  // Login user
  async login(username, password, rememberMe = false) {
    try {
      const response = await apiClient.post('/auth/login', {
        username,
        password,
        remember_me: rememberMe,
      });
      
      const { access_token, refresh_token, user } = response.data;
      const persistence = response.data.session_persistence || (rememberMe ? 'remember' : 'session');
      const useRemember = persistence === 'remember';

      persistSession({
        accessToken: access_token,
        refreshToken: refresh_token || null,
        user,
        rememberMe: useRemember,
        sessionMetadata: {
          session_persistence: persistence,
          access_expires_at: response.data.access_expires_at,
          refresh_expires_at: response.data.refresh_expires_at,
          session_expires_at: response.data.session_expires_at,
        },
      });
      
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Login failed' 
      };
    }
  },
  
  // Register user
  async register(username, email, password, rememberMe = false) {
    try {
      const response = await apiClient.post('/auth/register', {
        username,
        email,
        password,
        remember_me: rememberMe,
      });
      
      const { access_token, refresh_token, user } = response.data;
      const persistence = response.data.session_persistence || (rememberMe ? 'remember' : 'session');
      const useRemember = persistence === 'remember';

      persistSession({
        accessToken: access_token,
        refreshToken: refresh_token || null,
        user,
        rememberMe: useRemember,
        sessionMetadata: {
          session_persistence: persistence,
          access_expires_at: response.data.access_expires_at,
          refresh_expires_at: response.data.refresh_expires_at,
          session_expires_at: response.data.session_expires_at,
        },
      });
      
      return { success: true, user };
    } catch (error) {
      return { 
        success: false, 
        message: error.response?.data?.detail || 'Registration failed' 
      };
    }
  },
  
  // Logout user
  logout() {
    clearSession();
    notifyLogout();
  },
  
  // Get current user
  getCurrentUser() {
    const user = getStoredItem('user');
    if (!user) {
      return null;
    }
    try {
      return JSON.parse(user);
    } catch (error) {
      return null;
    }
  },

  getAccessToken() {
    return getAccessToken();
  },

  getSessionMetadata() {
    const metadata = getStoredItem('sessionMetadata');
    if (!metadata) {
      return null;
    }
    try {
      return JSON.parse(metadata);
    } catch (error) {
      return null;
    }
  },
  
  // Check if user is authenticated
  isAuthenticated() {
    const token = getAccessToken();
    if (!token) return false;
    
    // Check if token is expired
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      // Use UTC time for consistent token validation across timezones
      const currentTimeUTC = Math.floor(Date.now() / 1000);
      return payload.exp > currentTimeUTC;
    } catch (error) {
      return false;
    }
  },
  
  // Refresh token
  async refreshToken() {
    try {
      const refreshToken = getStoredItem('refreshToken');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      const response = await apiClient.post('/auth/refresh', {
        refresh_token: refreshToken
      });
      
      const { access_token, refresh_token: new_refresh_token } = response.data;

      updateSessionTokens({
        accessToken: access_token,
        refreshToken: new_refresh_token || null,
        sessionMetadata: {
          session_persistence: response.data.session_persistence,
          access_expires_at: response.data.access_expires_at,
          refresh_expires_at: response.data.refresh_expires_at,
          session_expires_at: response.data.session_expires_at,
        },
      });
      
      return { success: true, access_token };
    } catch (error) {
      clearSession();
      notifyLogout();
      return { success: false, message: 'Session expired. Please login again.' };
    }
  },
  
  // Validate token
  async validateToken() {
    try {
      const response = await apiClient.get('/auth/validate-token');
      return { valid: true, user: response.data.user };
    } catch (error) {
      return { valid: false, message: error.response?.data?.detail || 'Invalid token' };
    }
  }
};

export default authService;
